<template>
  <div class="publish-preview">
    <v-skeleton-loader v-if="loading" type="table-heading, table-row@3" class="rounded" />

    <div v-else-if="!hasPreview" class="ds-info-box ds-info-box--info">
      <v-icon class="ds-info-box__icon" small>mdi-information-outline</v-icon>
      <div class="ds-info-box__content">
        Run a preview to see which companies will be notified and how many recipients each one
        resolves to. Nothing is sent by a preview.
      </div>
    </div>

    <template v-else>
      <v-simple-table dense class="publish-preview__table">
        <template v-slot:default>
          <thead>
            <tr>
              <th class="text-left">Company</th>
              <th class="text-left">Eligibility</th>
              <th class="text-right">Recipients</th>
              <th class="text-right">Suppressed</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in rows" :key="row.id != null ? row.id : `row-${index}`">
              <td class="publish-preview__company">{{ row.name }}</td>
              <td>
                <v-chip x-small :color="row.eligible ? 'success' : 'error'" text-color="white">
                  {{ row.eligible ? 'Eligible' : 'Not eligible' }}
                </v-chip>
                <span v-if="row.reason" class="publish-preview__reason">{{ row.reason }}</span>
              </td>
              <td class="text-right">{{ row.recipientCount }}</td>
              <td class="text-right">{{ row.suppressedCount }}</td>
            </tr>
            <tr v-if="!rows.length">
              <td colspan="4" class="publish-preview__empty">
                The preview resolved no companies. Pick at least one company above.
              </td>
            </tr>
          </tbody>
          <tfoot v-if="rows.length">
            <tr class="publish-preview__totals">
              <td>Total</td>
              <td>{{ eligibleCount }} of {{ rows.length }} eligible</td>
              <td class="text-right">{{ totalRecipients }}</td>
              <td class="text-right">{{ totalSuppressed }}</td>
            </tr>
          </tfoot>
        </template>
      </v-simple-table>

      <div v-if="inAppRecipientCount > 0" class="ds-info-box ds-info-box--info mt-3">
        <v-icon class="ds-info-box__icon" small>mdi-bell-ring-outline</v-icon>
        <div class="ds-info-box__content">
          <strong>{{ inAppRecipientCount }}</strong>
          {{ inAppRecipientCount === 1 ? 'user' : 'users' }} will also get an in-app notification —
          the same audience, including anyone whose email is suppressed.
        </div>
      </div>

      <div v-if="reminderDueLabel" class="ds-info-box ds-info-box--info mt-3">
        <v-icon class="ds-info-box__icon" small>mdi-bell-outline</v-icon>
        <div class="ds-info-box__content">
          Deploy reminder will be due
          <strong>{{ reminderDueLabel }}</strong>
          <span v-if="reminderDueRelative" class="publish-preview__relative">
            ({{ reminderDueRelative }})
          </span>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
  import dayjs from 'dayjs';
  import relativeTime from 'dayjs/plugin/relativeTime';
  import { $formatDate } from '@Utils';

  dayjs.extend(relativeTime);

  /**
   * PublishPreviewTable — renders the result of `POST {id}/publish/preview`.
   *
   * The preview DTO is not pinned down by the plan, so every field is probed through a small
   * list of plausible spellings and falls back to a neutral value. In particular
   * `ReleaseSuppressionReason`'s members are UNKNOWN (addendum §A.4): we deliberately do not
   * define an enum here. A string reason is shown verbatim; a bare numeric code renders as
   * `Reason {code}` so a wrong label is never invented.
   */
  export default {
    name: 'PublishPreviewTable',

    props: {
      preview: { type: Object, default: null },
      loading: { type: Boolean, default: false },
    },

    computed: {
      hasPreview() {
        return !!this.preview;
      },

      rows() {
        if (!this.preview) return [];
        const source = this.preview.companies || this.preview.items || this.preview.results || [];
        if (!Array.isArray(source)) return [];
        return source.map((entry) => this.normaliseRow(entry));
      },

      eligibleCount() {
        return this.rows.filter((row) => row.eligible).length;
      },

      totalRecipients() {
        const declared = this.firstNumber([
          this.preview && this.preview.totalRecipients,
          this.preview && this.preview.recipientCount,
        ]);
        if (declared !== null) return declared;
        return this.rows.reduce((sum, row) => sum + row.recipientCount, 0);
      },

      totalSuppressed() {
        const declared = this.firstNumber([
          this.preview && this.preview.totalSuppressed,
          this.preview && this.preview.suppressedCount,
        ]);
        if (declared !== null) return declared;
        return this.rows.reduce((sum, row) => sum + row.suppressedCount, 0);
      },

      /**
       * How many people the in-app notification would reach. Zero when the publisher turned the
       * channel off — the server returns 0 in that case rather than a hypothetical count.
       */
      inAppRecipientCount() {
        const declared = this.firstNumber([this.preview && this.preview.inAppRecipientCount]);
        return declared === null ? 0 : declared;
      },

      /** ISO due date for the reminder this publish would arm, if the preview computed one. */
      reminderDueAt() {
        if (!this.preview) return null;
        const reminder = this.preview.reminder || {};
        const candidates = [this.preview.reminderDueAt, reminder.dueAt, reminder.dueAtUtc];
        return candidates.find((value) => !!value) || null;
      },

      reminderDueLabel() {
        if (!this.reminderDueAt) return '';
        return $formatDate(this.reminderDueAt, 'DD/MM/YYYY HH:mm');
      },

      reminderDueRelative() {
        if (!this.reminderDueAt) return '';
        const parsed = dayjs(this.reminderDueAt);
        return parsed.isValid() ? parsed.fromNow() : '';
      },
    },

    methods: {
      /**
       * Flattens one preview entry into the shape the table renders.
       * @param {object} entry raw server row
       */
      normaliseRow(entry) {
        const row = entry || {};
        const company = row.company || {};
        const eligible = [row.isEligible, row.eligible, company.isEligible].find(
          (value) => typeof value === 'boolean',
        );
        return {
          id: [row.companyId, row.id, company.id].find((value) => value != null),
          name: row.companyName || row.name || company.name || '(unnamed company)',
          // Absent eligibility is treated as eligible: the preview lists the companies that
          // WILL be notified, so an unflagged row is the normal case.
          eligible: eligible === undefined ? true : eligible,
          reason: this.reasonText(row),
          recipientCount:
            this.firstNumber([row.recipientCount, row.recipients, row.eligibleCount]) || 0,
          suppressedCount: this.firstNumber([row.suppressedCount, row.suppressed]) || 0,
        };
      },

      /**
       * Human-readable ineligibility/suppression reason.
       * A string is trusted as-is; a number is rendered as `Reason {code}` because the
       * `ReleaseSuppressionReason` members are undefined on the client (addendum §A.4).
       * @param {object} row
       * @returns {string}
       */
      reasonText(row) {
        const candidates = [
          row.reason,
          row.ineligibleReason,
          row.reasonText,
          row.suppressionReason,
          row.suppressionReasonText,
        ];
        const text = candidates.find(
          (value) => typeof value === 'string' && value.trim().length > 0,
        );
        if (text) return text.trim();

        const code = candidates.find((value) => typeof value === 'number');
        return code === undefined ? '' : `Reason ${code}`;
      },

      /**
       * First finite number in a candidate list, or null.
       * @param {Array} candidates
       */
      firstNumber(candidates) {
        const found = (candidates || []).find(
          (value) => Number.isFinite(Number(value)) && value !== null && value !== '',
        );
        return found === undefined ? null : Number(found);
      },
    },
  };
</script>

<style scoped lang="scss">
  .publish-preview__company {
    font-weight: 500;
  }

  .publish-preview__reason {
    margin-left: 8px;
    font-size: 0.75rem;
    opacity: 0.7;
  }

  .publish-preview__empty {
    text-align: center;
    opacity: 0.7;
    padding: 16px 0;
  }

  .publish-preview__totals {
    font-weight: 600;

    td {
      border-top: 1px solid var(--ds-border-subtle, rgba(255, 255, 255, 0.12));
    }
  }

  .publish-preview__relative {
    opacity: 0.7;
  }
</style>
